'use client';

import clsx from 'clsx';
import { Children, type CSSProperties, type ForwardedRef, type ReactNode } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { CommonProps } from '../types.js';
import { StepContext } from './context.js';

export interface UNSTABLE_StepperProps extends CommonProps {
  /** The steps to display in the stepper. */
  children: ReactNode;
  /** The index of the active step. */
  currentStep?: number;
  /** The callback to call when the current step changes. */
  onChange?: (step: number) => void;
  /** The orientation of the stepper.
   *
   * @default 'horizontal'
   */
  orientation?: 'vertical' | 'horizontal';
}

function UNSTABLE_Stepper(
  {
    children,
    currentStep,
    onChange,
    orientation = 'horizontal',
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: UNSTABLE_StepperProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const stepCount = Children.count(children);

  return (
    <div
      {...props}
      role="list"
      ref={ref}
      className={clsx('cim-stepper', UNSAFE_className)}
      style={{ ...UNSAFE_style, '--cim-stepper-step-count': stepCount } as CSSProperties}
      data-orientation={orientation}
    >
      {Children.map(children, (child, stepIndex) => {
        const isLast = stepIndex === stepCount - 1;

        return (
          <>
            <StepContext.Provider
              value={{
                orientation,
                stepCount: Children.count(children),
                currentStep: currentStep ?? -1,
                stepIndex,
                onChange:
                  onChange &&
                  (() => {
                    onChange(stepIndex);
                  }),
              }}
            >
              {child}
            </StepContext.Provider>
            {!isLast && orientation === 'horizontal' && <div className="cim-stepper-item-tail" role="presentation" />}
          </>
        );
      })}
    </div>
  );
}

/**
 * Displays a series of steps in a process.
 */
const _UNSTABLE_Stepper = withStyleProps(forwardRef(UNSTABLE_Stepper), 'Stepper');

export { _UNSTABLE_Stepper as UNSTABLE_Stepper };
